import { useCallback, useEffect, useState } from 'react';
import { hubService } from '../../services/hub.service';
import { errorText } from '../../lib/assistantView';
import {
  allowedModelsFor,
  differsFromDefault,
  hasOverride,
  modelForTask,
  promptSavePlan,
  textForTask,
  type PromptRow,
} from '../../lib/builderForm';

const input = 'rounded border border-gray-300 px-3 py-2 text-sm';

/**
 * Knowledge › Prompts (A2 step B): this bot's prompt and model per llm-tester task (ported from chatbot_front
 * hub/PromptConfig.js). Only the task instructions are editable — llm-tester keeps the safety guardrail and the output
 * format fixed. A build's own "Options" override these for that one run.
 */
export function PromptsPanel({ system, botId }: { system: string; botId: string }) {
  const [rows, setRows] = useState<PromptRow[] | null>(null);
  const [task, setTask] = useState('');
  const [text, setText] = useState('');
  const [model, setModel] = useState('');
  const [showDefault, setShowDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const load = useCallback(
    async (keepTask?: string) => {
      setError(null);
      try {
        const r = await hubService.knowledge<PromptRow[]>(system, botId, 'GET', 'prompts');
        const list = Array.isArray(r) ? r : [];
        setRows(list);
        const t = keepTask && list.some((x) => x.task === keepTask) ? keepTask : list[0]?.task ?? '';
        setTask(t);
        setText(textForTask(list, t));
        setModel(modelForTask(list, t));
      } catch (e) {
        setRows([]);
        setError(errorText(e));
      }
    },
    [system, botId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  function pick(t: string) {
    if (!rows) return;
    setTask(t);
    setText(textForTask(rows, t));
    setModel(modelForTask(rows, t));
    setSaved(null);
    setShowDefault(false);
  }

  async function save() {
    if (!rows) return;
    const plan = promptSavePlan(rows, task, text, model);
    if (!plan.reset && !plan.put) {
      setSaved('Nothing to save — this is the default.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (plan.reset) await hubService.knowledge(system, botId, 'DELETE', `prompts/${encodeURIComponent(task)}`);
      if (plan.put) await hubService.knowledge(system, botId, 'PUT', `prompts/${encodeURIComponent(task)}`, plan.put);
      setSaved('Saved.');
      await load(task);
    } catch (e) {
      setError(errorText(e));
      await load(task); // show what is stored after a partial failure
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    if (!window.confirm(`Put "${task}" back to the default prompt and model for this bot?`)) return;
    setSaving(true);
    setError(null);
    try {
      await hubService.knowledge(system, botId, 'DELETE', `prompts/${encodeURIComponent(task)}`);
      setSaved('Back to the default.');
      await load(task);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  if (rows === null) return <p className="text-sm text-gray-500">Loading…</p>;
  const row = rows.find((r) => r.task === task);
  const allowed = allowedModelsFor(rows, task);
  const overridden = hasOverride(rows, task) || !!row?.overrideModel;

  return (
    <section className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <div>
        <h2 className="font-medium text-gray-900">Prompts</h2>
        <p className="text-sm text-gray-600">
          This bot’s instructions and model for each Knowledge-builder task. Empty means the default; a build’s own options win for that build.
        </p>
      </div>
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</div>}
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">No tasks.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[14rem_1fr] gap-4">
          <ul className="space-y-1">
            {rows.map((r) => {
              const custom = hasOverride(rows, r.task) || !!r.overrideModel;
              return (
                <li key={r.task}>
                  <button
                    type="button"
                    onClick={() => pick(r.task)}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${r.task === task ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50'}`}
                  >
                    {r.title || r.task}
                    {custom && <span className="ml-1 text-xs text-amber-700">· this bot</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <code className="text-xs text-gray-500">{task}</code>
              <span className={`text-xs px-1.5 py-0.5 rounded ${hasOverride(rows, task) ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                prompt: {hasOverride(rows, task) ? 'this bot' : 'default'}
              </span>
              <span className={`text-xs px-1.5 py-0.5 rounded ${row?.overrideModel ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                model: {row?.overrideModel ? 'this bot' : 'default'}
              </span>
              {row?.tools?.length ? <span className="text-xs text-gray-500">uses tools: {row.tools.join(', ')}</span> : null}
            </div>
            <label className="text-sm flex items-center gap-2">
              Model
              {allowed.length ? (
                <select className={input} value={model} onChange={(e) => setModel(e.target.value)}>
                  {allowed.map((m) => (
                    <option key={m} value={m}>
                      {m}
                      {m === row?.defaultModel ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-gray-700">{model || '—'}</span>
              )}
            </label>
            <textarea className={`w-full ${input} font-mono text-xs`} rows={16} value={text} onChange={(e) => setText(e.target.value)} />
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <button type="button" className="px-3 py-1.5 bg-blue-600 text-white rounded disabled:opacity-50" disabled={saving} onClick={() => void save()}>
                {saving ? 'Saving…' : 'Save for this bot'}
              </button>
              {overridden && (
                <button type="button" className="text-red-600 hover:underline" disabled={saving} onClick={() => void reset()}>
                  Reset to default
                </button>
              )}
              <span className="text-xs text-gray-500">{differsFromDefault(rows, task, text) ? 'Differs from the default.' : 'Same as the default.'}</span>
              {saved && <span className="text-xs text-green-700">{saved}</span>}
              <button type="button" className="text-xs text-blue-600 hover:underline ml-auto" onClick={() => setShowDefault((v) => !v)}>
                {showDefault ? 'Hide' : 'Show'} the default
              </button>
            </div>
            {showDefault && <pre className="text-xs bg-gray-50 border rounded p-2 whitespace-pre-wrap max-h-80 overflow-auto">{row?.default}</pre>}
          </div>
        </div>
      )}
    </section>
  );
}
